<template>
  <div v-if="finishVerify && !waitRedirect">
    <div class="login-layout">
      <div class="login-logo" />
      <div class="login-img-hand">
        <img src="@/assets/images/login/login-bg-hand.png" alt="">
      </div>

      <!-- login-form brgin -->
      <div class="login-form">
        <div class="title">
          <h4>Welcome Back!</h4>
        </div>
        <ul>
          <li class="info-true" :class="formStatusClass.account">
            <input v-model="account.value" type="text" @blur="validateAccount" @keyup.enter="submitAsync">
            <div class="sub-title">
              {{ $t('login.account') }}
            </div>
            <p class="warning-depiction">
              {{ formErrorMessage.account }}
            </p>
          </li>
          <li class="info-true" :class="formStatusClass.password">
            <input v-model="password.value" type="password" @blur="validatePassword" @keyup.enter="submitAsync">
            <div class="sub-title">
              {{ $t('login.password') }}
            </div>
            <p class="warning-depiction">
              {{ formErrorMessage.password }}
            </p>
          </li>
        </ul>
        <vue-captcha ref="captcha" :sitekey="captchaSiteKey" type="recaptcha" :options="{ size: 'invisible' }" />
        <div class="item">
          <div class="flex align-items-start">
            <b-form-checkbox
              v-model="remember"
              name="checkbox-1"
            >
              {{ $t('login.rememberMe') }}
            </b-form-checkbox>
            <div>
              <a href="#" @click="onForgotPwdClick">{{ $t('login.forgetPassword') }}</a>
            </div>
          </div>
        </div>
        <div v-if="(formErrorMessage.captcha || '') !== ''">
          <span class="text-danger">{{ formErrorMessage.captcha }}</span>
        </div>
        <div v-if="(loginErrorMessage || '') !== ''">
          <span class="text-danger">{{ loginErrorMessage }}</span>
        </div>
        <div class="item">
          <button class="btn te-rounded-lg bg-primary" @click="submitAsync">
            <span class="text-white">{{ $t('login.login') }}</span>
          </button>
        </div>
        <div class="align-center">
          <span class="text-black-50">{{ versionInfo }}</span>
        </div>
      </div>
      <!-- login-form end -->
    </div>
    <ConfirmChangePassword
      v-if="passwordExpired.showConfirm"
      :data="passwordExpired.data"
      @close="closePasswordExpiredConfirm"
      @login="passwordExpiredConfirmLogin"
    />
  </div>
</template>

<script>
import * as AuthenticationService from '@/services/AuthenticationService';
import LoginStatusType from '@/objects/LoginStatusType';
import ConfirmChangePassword from '@/components/Login/ConfirmChangePassword.vue';

export default {
  components: {
    ConfirmChangePassword
  },
  props: ['redirect'],
  data() {
    return {
      account: {
        value: '',
        error: ''
      },
      password: {
        value: '',
        error: ''
      },
      captcha: {
        value: '',
        error: '',
        info: {}
      },
      remember: false,
      loginStatus: '',

      finishVerify: false,
      waitRedirect: false,

      submitting: false,

      showChangePassword: false,

      passwordExpired: {
        data: {},
        showConfirm: false
      }
    };
  },
  computed: {
    userId() {
      return this.$store.getters.userId;
    },
    versionInfo() {
      if (process.env.VUE_APP_VERSION) {
        return `Ver ${process.env.VUE_APP_VERSION}`;
      }
      return '';
    },
    loginErrorMessage() {
      let result;
      switch (this.loginStatus) {
        case 'failed':
          result = this.$t('login.messages.accountOrPasswordError');
          break;
        case 'timeout':
          result = this.$t('login.messages.loginTimeout');
          break;
        default:
          break;
      }
      return result;
    },
    formErrorMessage() {
      let account;
      switch (this.account.error) {
        case 'required':
          account = this.$t('login.messages.requiredAccount');
          break;
        default:
          break;
      }

      let password;
      switch (this.password.error) {
        case 'required':
          password = this.$t('login.messages.requiredPassword');
          break;
        default:
          break;
      }

      let captcha;
      switch (this.captcha.error) {
        case 'required':
          captcha = this.$t('login.messages.requiredCaptcha');
          break;
        case 'invalid':
          captcha = this.$t('login.messages.captchaInvalid');
          break;
        default:
          break;
      }

      return {
        account, password, captcha
      };
    },
    formStatusClass() {
      return {
        account: {
          warning: (this.account.error || '') !== ''
        },
        password: {
          warning: (this.password.error || '') !== ''
        },
        captcha: {
          'info-true': (this.captcha.value || '') !== '',
          warning: (this.captcha.error || '') !== ''
        }
      };
    },
    captchaSiteKey() {
      return this.$env.VUE_APP_Captcha_SiteKey;
    }
  },
  watch: {
    userId(val) {
      if ((val !== '')) {
        this.redirectPath();
      }
    }
  },
  created() {
    this.processLogoutStatus();
  },
  async beforeCreate() {
    try {
      this.$store.dispatch('loadLoginStatus');
      const status = await AuthenticationService.VerifyAsync();
      if (status !== LoginStatusType.Success) {
        await AuthenticationService.LogoutAsync();
      } else {
        await this.redirectPath();
      }
    } finally {
      this.finishVerify = true;
    }
  },
  methods: {
    resetCaptcha() {
      if (this.$refs.captcha) {
        this.$refs.captcha.ResetAsync();
      }
      this.captcha.value = '';
    },
    onForgotPwdClick() {
      this.$router.push({ path: '/Forgot' });
    },
    openPasswordExpiredConfirm(data) {
      this.passwordExpired.showConfirm = true;
      this.passwordExpired.data = data;
    },
    closePasswordExpiredConfirm() {
      this.passwordExpired.showConfirm = false;
      this.passwordExpired.data = {};
    },
    passwordExpiredConfirmLogin() {
      this.LoginByRefreshTokenAsync();
    },
    validateAccount() {
      this.account.error = '';
      if ((this.account.value || '') === '') this.account.error = 'required';
      return this.account.error === '';
    },
    validatePassword() {
      this.password.error = '';
      if ((this.password.value || '') === '') this.password.error = 'required';
      return this.password.error === '';
    },
    validateCaptcha() {
      this.captcha.error = '';
      if ((this.captcha.value || '') === '') this.captcha.error = 'required';
      return this.captcha.error === '';
    },
    async VerifyCaptchaAsync() {
      const {
        token, error, siteKey, type
      } = await this.$refs.captcha.VerifyAsync();
      this.captcha.error = error || '';
      if ((this.captcha.error || '') === '') {
        this.captcha.info = { token, siteKey, type };
      }
      return this.captcha.error === '';
    },
    async validateAsync() {
      if ([this.validateAccount(), this.validatePassword()].every((v) => v)) {
        const valid = await this.VerifyCaptchaAsync();
        return valid;
      }
      return false;
    },
    async submitAsync() {
      if (!this.submitting) {
        try {
          this.submitting = true;
          let status = LoginStatusType.Failed;
          await this.$loading(async () => {
            if (!await this.validateAsync()) {
              return;
            }
            this.$store.dispatch('setRememberStatus', this.remember);
            status = await AuthenticationService.LoginAsync(this.account.value, this.password.value, this.captcha.info);
            this.processLoginStatus(status, 'login');
          }, 0);
        } finally {
          this.submitting = false;
        }
      }
    },
    alertPasswordExpired() {
      this.$popup.alert(this.$t('login.messages.passwordExpiredAlert'));
    },
    alertAccountLocked(data) {
      this.$popup.alert(this.$t('login.messages.accountLocked', { times: data.times }));
    },
    processLoginStatus(status, type) {
      this.loginStatus = '';
      switch (status.code) {
        case LoginStatusType.AlreadyLogin.code:
          this.openAleadyLoginConfirm();
          break;
        case LoginStatusType.Success.code:
          this.redirectPath();
          break;
        case LoginStatusType.PasswordExpired.code:
          this.openPasswordExpiredConfirm(status.data);
          break;
        case LoginStatusType.AccountLocked.code:
          this.alertAccountLocked(status.data);
          break;
        default:
          switch (status.code) {
            case LoginStatusType.Failed.code:
              switch (type) {
                case 'login':
                  this.loginStatus = 'failed';
                  break;
                case 'reset':
                  this.loginStatus = 'timeout';
                  break;
                default:
                  break;
              }
              break;
            default:
              break;
          }

          this.$store.dispatch('initialLoginStatus');
          break;
      }
      this.resetCaptcha();
      this.password.value = '';
    },
    async redirectPath() {
      if (!this.waitRedirect) {
        this.waitRedirect = true;
        try {
          await this.$router.push({ path: this.redirect || '/' });
        } catch (err) {
          this.waitRedirect = false;
        }
      }
    },
    openAleadyLoginConfirm() {
      const message = `<p>${this.$t('login.messages.alreadyLogin')}</p>`;
      this.$popup.confirmAsync(message).then(async (state) => {
        await this.LoginByRefreshTokenAsync(state);
      });
    },
    async LoginByRefreshTokenAsync(state = true) {
      await this.$loading(async () => {
        let status = LoginStatusType.Cancel;
        if (state) {
          status = await AuthenticationService.RefreshAsync();
        }
        this.processLoginStatus(status, 'reset');
      });
    },
    processLogoutStatus() {
      const status = this.$store.getters.logoutStatus || {};
      switch (status.code) {
        case LoginStatusType.PasswordExpired.code:
          this.alertPasswordExpired(status.data);
          break;
        case LoginStatusType.AccountLocked.code:
          this.alertAccountLocked(status.data);
          break;
        default:
          break;
      }

      this.$store.commit('setLogoutStatus');
    }
  }
};
</script>
